import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { ResponseKeyValueItem } from 'components/transactionEditor/ResponseKeyValueList';
import styles from 'components/transactionEditor/ActionButtons.module.css';
import i18n from 'i18n';
import BzmIcon from '../icons/BzmIcon';
import { DropdownButtonItem } from '../buttons/BzDropdownButton';
import { MessagePropertyType, messagePropertyTypes } from '../../types/rest/dslTypes';
import PropertyTypeDropdown from './PropertyTypeDropdown';
import _ from 'lodash';
import BzFormControlWithHook from '../forms/BzFormControlWithHook';
import { CallbackFn, useFormInput, ValidatorType } from '../../hooks/useFormInput';
import { createAiAssistantIcon } from '../../views/AssetCatalog/AiAssistant/aiAssistantUtil';
import AiAssistantModal from '../../views/AssetCatalog/AiAssistant/AiAssistantModal';
import { getAccountAiConsent } from '../../utils/bzmUserConfigUtil';
import { clearConversationId } from '../../store/aiConversations/aiConversationsActions';
import ValueEditorModal from './ValueEditorModal';

interface ResponseKeyValueProps {
  id: string;
  responseItem: ResponseKeyValueItem;
  onChange: (name: string, value: string, id: string) => void;
  onDelete: () => void;
  onAdd: () => void;
  displayAdd: boolean;
  isReadOnly?: boolean;
  validateName?: boolean;
  isMessageProperty?: boolean;
  enableAiAssistant?: boolean; // Flag to enable AI assistant functionality
  transactionId?: number;
  multilineValue?: boolean;
}

const ResponseKeyValue = (props: ResponseKeyValueProps) => {
  const dispatch = useDispatch();
  const [showAiAssistant, setShowAiAssistant] = useState(false);
  const [aiAssistantCallerId, setAiAssistantCallerId] = useState<string>('');

  const [showValueEditor, setShowValueEditor] = useState(false);

  const handleChange = (e: any, id: string) => {
    const target = e.target as HTMLInputElement;
    props.onChange(target.name, target.value, id);
  };

  const resolvedDropdownMessagePropertyTypes = messagePropertyTypes;

  const defaultType: DropdownButtonItem = {
    key: MessagePropertyType.STRING,
    title: messagePropertyTypes[MessagePropertyType.STRING],
  };

  const propertyTypeToSelect: DropdownButtonItem =
    props.responseItem && props.responseItem.type
      ? {
          key: props.responseItem.type,
          title: messagePropertyTypes[props.responseItem.type],
        }
      : defaultType;

  const dropdownPropertyTypes = _.map(resolvedDropdownMessagePropertyTypes, (item, key) => {
    return MessagePropertyType[key];
  });

  const nameInput = useFormInput(
    props.responseItem.name,
    (fn: CallbackFn) => props.onChange('name', fn.value.toString(), props.id),
    { type: ValidatorType.REQUIRED, invalidMessage: i18n.t('recordings:editor.messages.nameValueRequired') },
    {
      type: ValidatorType.MAX_LENGTH,
      value: 250,
      invalidMessage: i18n.t('recordings:editor.messages.nameValueMaxLength'),
    },
    {
      type: ValidatorType.INVALID_LEADING_SPACES,
      invalidMessage: i18n.t('recordings:editor.messages.nameValueIsInvalid'),
    },
    {
      type: ValidatorType.INVALID_CHARACTERS_MEDIUM,
      invalidMessage: i18n.t('recordings:editor.messages.nameValueIsInvalid'),
    },
  );

  // AI Assistant functionality
  const shouldShowAiIcon = props.enableAiAssistant && !props.isReadOnly && getAccountAiConsent();
  const stableNewTxSuffix = React.useRef(`new-${Date.now()}`).current;
  const valueFieldId =
    props.transactionId && props.transactionId !== -1
      ? `qa-response-value-${props.transactionId}-${props.id}`
      : `qa-response-value-${stableNewTxSuffix}-${props.id}`;

  const handleAiAssistantShow = (callerId: string) => {
    setAiAssistantCallerId(callerId);
    setShowAiAssistant(true);
  };

  const handleAiAssistantUpdate = (value: string) => {
    props.onChange('value', value, props.id);
  };

  const handleDelete = () => {
    // Clear the AI conversation for this field ID
    if (props.enableAiAssistant) {
      dispatch(clearConversationId(valueFieldId));
    }
    // Call the original delete handler
    props.onDelete();
  };

  const currentValue = props.responseItem.value ? props.responseItem.value : '';

  return (
    <>
      <Row id={props.id} className="mb-3">
        <Col md={3}>
          <Form.Group controlId={'qa-input-resp-header-name-' + props.id}>
            <Form.Label>Name</Form.Label>
            {props.validateName === true ? (
              <BzFormControlWithHook
                name="name"
                autoComplete="off"
                disabled={false}
                useFormInputHook={nameInput}
                placeholder={i18n.t('recordings:table.name')}
                required
              />
            ) : (
              <Form.Control
                name="name"
                as="input"
                disabled={props.isReadOnly}
                onChange={(event: any) => handleChange(event, props.id)}
                placeholder={i18n.t('transactions:txn.name')}
                value={props.responseItem.name ? props.responseItem.name : ''}
              />
            )}
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId={'qa-input-resp-header-value-' + props.id}>
            <Form.Label>Value</Form.Label>
            <div className={'position-relative'}>
              <Form.Control
                id={valueFieldId}
                name="value"
                disabled={props.isReadOnly}
                as={props.multilineValue ? 'textarea' : 'input'}
                {...(props.multilineValue ? { rows: 1 } : {})}
                onChange={(event: any) => handleChange(event, props.id)}
                placeholder={i18n.t('transactions:editor.value')}
                value={currentValue}
                style={{
                  paddingRight: shouldShowAiIcon ? '55px' : '35px',
                  ...(props.multilineValue ? { resize: 'none', overflow: 'hidden' } : {}),
                }}
              />
              <span className="position-absolute d-inline-flex align-items-center" style={{ right: '7px', top: '2px' }}>
                <span
                  role="button"
                  tabIndex={0}
                  style={{ fontSize: '18px', color: '#3272D9', cursor: 'pointer', marginTop: '2px' }}
                  onClick={() => setShowValueEditor(true)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setShowValueEditor(true);
                    }
                  }}
                >
                  <BzmIcon title={'Validate'} className="text-primary" variant={'icon-bzm-code'} />
                </span>
                {shouldShowAiIcon && (
                  <span
                    role="button"
                    tabIndex={0}
                    style={{
                      fontSize: '18px',
                      color: '#3272D9',
                      cursor: 'pointer',
                      marginLeft: '4px',
                    }}
                    onClick={() => handleAiAssistantShow(valueFieldId)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleAiAssistantShow(valueFieldId);
                      }
                    }}
                  >
                    {createAiAssistantIcon()}
                  </span>
                )}
              </span>
            </div>
          </Form.Group>
        </Col>
        {props.isMessageProperty === true && (
          <Col md={3}>
            <Form.Group controlId={'qa-input-resp-properties-value-' + props.id}>
              <Form.Label>Type</Form.Label>
              <PropertyTypeDropdown
                isReadOnly={props.isReadOnly}
                selected={propertyTypeToSelect}
                propertyTypes={dropdownPropertyTypes}
                onSelect={(item: DropdownButtonItem) => props.onChange('type', item.key, props.id)}
                id={'propType-' + props.id}
                isShortTitle={false}
              />
            </Form.Group>
          </Col>
        )}

        <Col md={2}>
          <div className={styles.actionButtons}>
            <Button
              disabled={props.isReadOnly}
              id={'qa-btn-delete-' + props.id}
              onClick={handleDelete}
              variant="danger"
              className="btn-circular"
              title={i18n.t('transactions:editor.deleteResponseHeader')}
            >
              <BzmIcon variant={'icon-bzm-delete'} />
            </Button>
            {props.displayAdd && (
              <Button
                disabled={props.isReadOnly}
                id={'qa-btn-add-resp-header' + props.id}
                onClick={props.onAdd}
                variant="secondary"
                className="btn-circular ml-4"
                title={i18n.t('transactions:editor.addResponseHeader')}
              >
                <BzmIcon variant={'icon-bzm-add'} className={'icon-theme-blue'} />
              </Button>
            )}
          </div>
        </Col>
      </Row>
      {showAiAssistant && (
        <AiAssistantModal
          callerId={aiAssistantCallerId}
          updateFunction={handleAiAssistantUpdate}
          currentValue={currentValue}
          onClose={() => setShowAiAssistant(false)}
          title="Response Header"
        />
      )}
      {showValueEditor && (
        <ValueEditorModal
          value={currentValue}
          onSave={value => props.onChange('value', value, props.id)}
          onClose={() => setShowValueEditor(false)}
        />
      )}
    </>
  );
};

export default ResponseKeyValue;
